import React, { useState } from 'react'
import { Box, Typography, Collapse, RadioGroup, FormControlLabel, Radio, Checkbox, TextField, Button, CircularProgress, IconButton, InputAdornment } from '@mui/material'
import axios from 'axios'
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined'
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined'
import AlternateEmailOutlinedIcon from '@mui/icons-material/AlternateEmailOutlined'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import { useAppTheme } from '@/theme/ThemeContext'
import type { ThemeMode } from '@/theme/ThemeContext'
import { passwordChangeErrorMessage } from '@/services/authErrors'
import SectionHeader from './SectionHeader'
import OpenEyeIcon from '@/assets/OpenEye icon.svg'
import EyeIcon from '@/assets/eye icon.svg'

const ModePreview = ({ mode }: { mode: 'light' | 'dark' }) => {
  const isDark = mode === 'dark'
  const bg = isDark ? '#1A202C' : '#FFFFFF'
  const sidebar = isDark ? '#2D3748' : '#F3F4F6'
  const header = isDark ? '#2D3748' : '#FFFFFF'
  const card = isDark ? '#2D3748' : '#F9FAFB'
  const outline = isDark ? '#4A5568' : '#E5E7EB'

  return (
    <Box sx={{ display: 'flex', width: '100%', height: '100%', bgcolor: bg, borderRadius: 1, overflow: 'hidden', border: `1px solid ${outline}` }}>
      {/* Sidebar */}
      <Box sx={{ width: '30%', bgcolor: sidebar, borderRight: `1px solid ${outline}`, p: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Box sx={{ width: '60%', height: 6, bgcolor: outline, borderRadius: 1 }} />
        <Box sx={{ width: '80%', height: 6, bgcolor: outline, borderRadius: 1, mt: 1 }} />
        <Box sx={{ width: '70%', height: 6, bgcolor: outline, borderRadius: 1 }} />
      </Box>
      {/* Content */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box sx={{ height: '20%', bgcolor: header, borderBottom: `1px solid ${outline}`, display: 'flex', alignItems: 'center', px: 1, justifyContent: 'space-between' }}>
           <Box sx={{ width: '20%', height: 4, bgcolor: outline, borderRadius: 1 }} />
           <Box sx={{ width: 10, height: 10, bgcolor: outline, borderRadius: '50%' }} />
        </Box>
        {/* Body */}
        <Box sx={{ p: 1, flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', gap: 1, height: '40%' }}>
             <Box sx={{ flex: 1, bgcolor: card, borderRadius: 0.5, border: `1px solid ${outline}` }} />
             <Box sx={{ flex: 1, bgcolor: card, borderRadius: 0.5, border: `1px solid ${outline}` }} />
          </Box>
          <Box sx={{ flex: 1, bgcolor: card, borderRadius: 0.5, border: `1px solid ${outline}` }} />
        </Box>
      </Box>
    </Box>
  )
}

const SplitPreview = () => {
  return (
    <Box sx={{ display: 'flex', width: '100%', height: '100%', borderRadius: 1, overflow: 'hidden' }}>
       <Box sx={{ flex: 1, position: 'relative' }}>
          <ModePreview mode="dark" />
          <Box sx={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: '50%', overflow: 'hidden' }}>
             <Box sx={{ width: '200%', height: '100%', transform: 'translateX(50%)' }}>
               <ModePreview mode="light" />
             </Box>
          </Box>
       </Box>
    </Box>
  )
}

type PanelKey = 'displayMode' | 'tableRows' | 'emailAlerts' | 'passwordReset' | null

const UsersSettings = () => {
  const { themeMode, setThemeMode, mode: currentMode } = useAppTheme()
  const isDarkMode = currentMode === 'dark'
  const BORDER = isDarkMode ? '#374151' : '#E4E7EB'

  // Manage open state
  const [openPanel, setOpenPanel] = useState<PanelKey>('displayMode')

  const toggle = (key: PanelKey) => {
    setOpenPanel(prev => (prev === key ? null : key))
  }

  // Manage Table Rows state (Persisted in localStorage)
  const [tableRows, setTableRows] = useState(() => localStorage.getItem('table-rows-preference') || '50')
  const changeTableRows = (val: string) => {
    setTableRows(val)
    localStorage.setItem('table-rows-preference', val)
  }

  // Manage password change state
  const userName = localStorage.getItem('loggedInUserName') || 'רימונים'
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordLoading, setPasswordLoading] = useState(false)
  const [passwordError, setPasswordError] = useState('')
  const [passwordSuccess, setPasswordSuccess] = useState('')
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const handlePasswordReset = () => {
    setCurrentPassword('')
    setNewPassword('')
    setConfirmPassword('')
    setPasswordError('')
    setPasswordSuccess('')
    setShowCurrentPassword(false)
    setShowNewPassword(false)
    setShowConfirmPassword(false)
  }

  const handlePasswordSave = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('נא למלא את כל השדות')
      return
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('הסיסמאות אינן תואמות')
      return
    }

    setPasswordLoading(true)
    setPasswordError('')
    setPasswordSuccess('')

    try {
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}/api/user/password`,
        {
          oldPassword: currentPassword,
          newPassword,
        },
        // The Gateway takes the user from the token, ends every session issued before the change and renews the one whose token it receives.
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`,
          },
        }
      )
      const renewedToken = response.data?.data?.token
      if (renewedToken) localStorage.setItem('authToken', renewedToken)
      setPasswordSuccess('הסיסמה שונתה בהצלחה')
      setTimeout(() => handlePasswordReset(), 3000)
    } catch (err) {
      setPasswordError(passwordChangeErrorMessage(err))
    } finally {
      setPasswordLoading(false)
    }
  }

  const renderCard = (title: string, value: ThemeMode, Preview: React.ReactNode) => {
    const active = themeMode === value

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
          <Box
          onClick={() => setThemeMode(value)}
          sx={{ 
            width: 'clamp(180px, 16vw, 280px)',
            height: 'clamp(100px, 8.5vw, 150px)',
            p:0.3,
            border: `2px solid ${active ? '#3B82F6' : 'transparent'}`, 
            borderRadius: 2,
            cursor: 'pointer',
            transition: 'all 0.2s',
            boxShadow: active ? '0 0 0 1px #3B82F6' : `0 0 0 1px ${isDarkMode ? '#374151' : '#E5E7EB'}`,
            '&:hover': {
               boxShadow: active ? '0 0 0 1px #3B82F6' : `0 0 0 1px ${isDarkMode ? '#6B7280' : '#D1D5DB'}`,
            }
          }}
        >
          {Preview}
        </Box>
        <Typography sx={{ color: active ? '#3B82F6' : (isDarkMode ? '#D1D5DB' : '#4B5563'), fontWeight: active ? 600 : 400, fontSize: 'clamp(12px, 0.9vw, 16px)' }}>
          {title}
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ mt: 'clamp(12px, 2vw, 32px)', width: '100%', direction: 'rtl', minHeight: 0 }}>
      <Box py="clamp(12px, 1.2vw, 20px)" sx={{ borderBottom: `1px solid ${BORDER}` }}>
        <SectionHeader
          title="מצב תצוגה"
          icon={<LightModeOutlinedIcon fontSize="small" />}
          isOpen={openPanel === 'displayMode'}
          onExpandClick={() => toggle('displayMode')}
        />
        <Collapse in={openPanel === 'displayMode'} unmountOnExit>
          <Box pt="clamp(20px, 3vw, 40px)" pb="clamp(8px, 1vw, 16px)">
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 'clamp(20px, 4vw, 64px)', flexWrap: 'wrap' }}>
              {renderCard('בהיר', 'light', <ModePreview mode="light" />)}
              {renderCard('כהה', 'dark', <ModePreview mode="dark" />)}
              {renderCard('ברירת מחדל דפדפן', 'system', <SplitPreview />)}
            </Box>
          </Box>
        </Collapse>
      </Box>

      <Box py="clamp(12px, 1.2vw, 20px)" sx={{ borderBottom: `1px solid ${BORDER}` }}>
        <SectionHeader
          title="כמות שורות לעמוד בטבלאות"
          icon={<TableChartOutlinedIcon fontSize="small" />}
          isOpen={openPanel === 'tableRows'}
          onExpandClick={() => toggle('tableRows')}
        />
        <Collapse in={openPanel === 'tableRows'} unmountOnExit>
          <Box pt="clamp(20px, 3vw, 40px)" pb="clamp(8px, 1vw, 16px)">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Typography sx={{ color: isDarkMode ? '#E5E7EB' : '#4B5563', fontSize: 14 }}>הצג</Typography>
              <RadioGroup row value={tableRows} onChange={(e) => changeTableRows(e.target.value)}>
                {['25', '50', '100'].map((val) => (
                  <FormControlLabel
                    key={val}
                    value={val}
                    control={<Radio size="small" sx={{ color: isDarkMode ? '#4B5563' : '#D1D5DB', '&.Mui-checked': { color: '#84C081' } }} />}
                    label={<Typography sx={{ fontSize: 14, color: isDarkMode ? '#E5E7EB' : '#1F2937' }}>{val}</Typography>}
                    labelPlacement="start"
                    sx={{ ml: 4, mr: 0 }}
                  />
                ))}
              </RadioGroup>
            </Box>
            <Typography sx={{ color: isDarkMode ? '#9CA3AF' : '#9CA3AF', fontSize: 13, mt: 1 }}>
              ניתן לשנות בכל פעם את הכמות לתצוגה, אבל כאן ניתן לבחור מה תהיה ברירת המחדל
            </Typography>
          </Box>
        </Collapse>
      </Box>

      <Box py="clamp(12px, 1.2vw, 20px)" sx={{ borderBottom: `1px solid ${BORDER}` }}>
        <SectionHeader
          title="קבלת התראות למייל"
          icon={<AlternateEmailOutlinedIcon fontSize="small" />}
          isOpen={openPanel === 'emailAlerts'}
          onExpandClick={() => toggle('emailAlerts')}
        />
        <Collapse in={openPanel === 'emailAlerts'} unmountOnExit>
          <Box pt="clamp(20px, 3vw, 40px)" pb="clamp(8px, 1vw, 16px)">
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <FormControlLabel 
                control={<Checkbox defaultChecked sx={{ color: isDarkMode ? '#4B5563' : '#D1D5DB', '&.Mui-checked': { color: '#84C081' } }} />} 
                label={<Typography sx={{ fontSize: 14, color: isDarkMode ? '#E5E7EB' : '#1F2937' }}>שיוך משימה למשתמש שלי ב- CRM</Typography>} 
              />
              <FormControlLabel 
                control={<Checkbox sx={{ color: isDarkMode ? '#4B5563' : '#D1D5DB', '&.Mui-checked': { color: '#84C081' } }} />} 
                label={<Typography sx={{ fontSize: 14, color: isDarkMode ? '#E5E7EB' : '#1F2937' }}>שינוי במשימה שפתחתי ב- CRM</Typography>} 
              />
            </Box>
          </Box>
        </Collapse>
      </Box>

      <Box py="clamp(12px, 1.2vw, 20px)" sx={{ borderBottom: `1px solid ${BORDER}` }}>
        <SectionHeader
          title="שינוי סיסמא"
          icon={<LockOutlinedIcon fontSize="small" />}
          isOpen={openPanel === 'passwordReset'}
          onExpandClick={() => toggle('passwordReset')}
        />
        <Collapse in={openPanel === 'passwordReset'} unmountOnExit>
          <Box pt="clamp(20px, 3vw, 40px)" pb="clamp(8px, 1vw, 16px)">
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 320 }}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Typography sx={{ fontSize: 14, color: isDarkMode ? '#E5E7EB' : '#4B5563' }}>שם משתמש</Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: isDarkMode ? '#FFFFFF' : '#1F2937' }}>{userName}</Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography sx={{ fontSize: 13, color: isDarkMode ? '#E5E7EB' : '#4B5563' }}>סיסמא נוכחית</Typography>
                <TextField
                  size="small"
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  sx={{ bgcolor: isDarkMode ? '#1e293b' : '#fff' }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                          edge="end"
                          size="small"
                        >
                          <img
                            src={showCurrentPassword ? OpenEyeIcon : EyeIcon}
                            alt="toggle password visibility"
                            style={{
                              width: 20,
                              height: 20,
                              filter: isDarkMode ? 'brightness(0) invert(1)' : 'none',
                            }}
                          />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography sx={{ fontSize: 13, color: isDarkMode ? '#E5E7EB' : '#4B5563' }}>סיסמא חדשה</Typography>
                <TextField
                  size="small"
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  sx={{ bgcolor: isDarkMode ? '#1e293b' : '#fff' }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          edge="end"
                          size="small"
                        >
                          <img
                            src={showNewPassword ? OpenEyeIcon : EyeIcon}
                            alt="toggle password visibility"
                            style={{
                              width: 20,
                              height: 20,
                              filter: isDarkMode ? 'brightness(0) invert(1)' : 'none',
                            }}
                          />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography sx={{ fontSize: 13, color: isDarkMode ? '#E5E7EB' : '#4B5563' }}>אימות סיסמא חדשה</Typography>
                <TextField
                  size="small"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  sx={{ bgcolor: isDarkMode ? '#1e293b' : '#fff' }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                          size="small"
                        >
                          <img
                            src={showConfirmPassword ? OpenEyeIcon : EyeIcon}
                            alt="toggle password visibility"
                            style={{
                              width: 20,
                              height: 20,
                              filter: isDarkMode ? 'brightness(0) invert(1)' : 'none',
                            }}
                          />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              {passwordError && (
                <Typography sx={{ color: '#E53935', fontSize: 13 }}>{passwordError}</Typography>
              )}
              {passwordSuccess && (
                <Typography sx={{ color: '#4CAF50', fontSize: 13 }}>{passwordSuccess}</Typography>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 2, mt: 2, flexDirection: 'row-reverse' }}>
                <Button onClick={handlePasswordReset} variant="text" sx={{ color: '#9CA3AF', minWidth: 'auto', px: 2, '&:hover': { bgcolor: 'transparent' } }}>נקה הכל</Button>
                <Button onClick={handlePasswordSave} disabled={passwordLoading} variant="contained" sx={{ bgcolor: '#E1F3FA', color: '#003F61', boxShadow: 'none',  '&:hover': { bgcolor: '#D0EAF4', boxShadow: 'none' } }}>
                  {passwordLoading ? <CircularProgress size={20} /> : 'שמירת שינויים'}
                </Button>
              </Box>
            </Box>
          </Box>
        </Collapse>
      </Box>
    </Box>
  )
}

export default UsersSettings
